import React, { useState, useEffect, useCallback } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import states from '../../../components/common/states';
import ViewModal from '../../../components/common/ViewModal';

const SaleContractForm = () => {
  const { id } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [closedContracts, setClosedContracts] = useState([]);
  const [formData, setFormData] = useState({
    sales_contract_no: '',
    contract_date: '',
    trade_date: '',
    type_of_transaction: '',
    tel: '',
    domestic_buyer_id: '',
    commodity_id: '',
    emd: '',
    contract_quantity_mt: '',
    sale_price_per_mt: '',
    delivery_point: '',
    delivery_start_date: '',
    delivery_end_date: '',
    payment_terms: '',
    broker_id: '',
    penalty_clause: '',
    arbitration_clause: '',
    remarks: '',
    created_by: '',
    order_id: '',
    cma_id: '',
    warehouse_id: '',
    cha_cma_id: '',
    kribhco_state_code: '',
    warehouse_charges: '',
    debit_notes: [{ dn_of_account: '', dn_date: '', dn_amount: '' }],
    closing_date: '',
    vessel_name: ''
  });
  
  const [originalContractQuantity, setOriginalContractQuantity] = useState(0);
  const [buyers, setBuyers] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [filteredCommodities, setFilteredCommodities] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [importOrders, setImportOrders] = useState([]);
  const [cmas, setCmas] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [selectedPurchaseContract, setSelectedPurchaseContract] = useState(null);
  const [selectedType, setSelectedType] = useState('CMA');
  const [chas, setChas] = useState([]);
  const [chaWarehouses, setChaWarehouses] = useState([]);
  const [vessels, setVessels] = useState([]);
  const navigate = useNavigate();

  const handleViewClosedContracts = async () => {
    if (!selectedPurchaseContract || !selectedPurchaseContract.order_id) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/masters/closed-contracts-by-order?order_id=${selectedPurchaseContract.order_id}`, { credentials: 'include' });
      if (response.ok) {
        const result = await response.json();
        setClosedContracts(result.data || []);
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching closed contracts:', error);
    }
  };

  const fetchCommoditiesByPurchaseContract = useCallback(async (orderId) => {
    if (!orderId) {
      setFilteredCommodities([]);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-commodities-by-purchase-contract?order_id=${orderId}`, { credentials: 'include' });
      const data = await res.json();
      const commoditiesList = data.data || [];
      setFilteredCommodities(commoditiesList);
      
      if (commoditiesList.length === 1 && !id) {
        setFormData(prev => ({ 
          ...prev, 
          commodity_id: commoditiesList[0].commodity_id 
        }));
      }
    } catch (err) {
      console.error('Error fetching commodities by purchase contract:', err);
      setFilteredCommodities([]);
    }
  }, [id]);

  const fetchContractForEdit = useCallback(async (contractId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/sales/sale-contract/${contractId}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch contract');
      const result = await res.json();
      if (result.data) {
        const isCha = result.data.cma_type === 'CHA';
        setSelectedType(isCha ? 'CHA' : 'CMA');
        
        const idToUse = result.data.cha_cma_id;
        
        if (idToUse) {
          const endpoint = isCha 
            ? `${API_BASE_URL}/api/masters/dropdown-warehouse-list-by-cha?cha_cma_id=${idToUse}`
            : `${API_BASE_URL}/api/masters/dropdown-warehouse-list-by-cma?cha_cma_id=${idToUse}`;
          
          const warehousesRes = await fetch(endpoint, { credentials: 'include' });
          const warehousesData = await warehousesRes.json();
          
          if (isCha) {
            setChaWarehouses(warehousesData.data || []);
          } else {
            setWarehouses(warehousesData.data || []);
          }
        }

        setOriginalContractQuantity(parseFloat(result.data.contract_quantity_mt || 0));

        // Helper function to format date for input field (YYYY-MM-DD)
        const formatDateForInput = (dateString) => {
          if (!dateString) return '';
          try {
            // Handle different date formats from database
            let date;
            if (typeof dateString === 'string') {
              // If already in YYYY-MM-DD format, return as is
              if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
                return dateString;
              }
              // Parse the date string
              date = new Date(dateString);
            } else {
              date = dateString;
            }
            
            // Check if date is valid
            if (isNaN(date.getTime())) {
              return '';
            }
            
            // Format as YYYY-MM-DD (timezone-safe)
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          } catch (error) {
            console.error('Error formatting date:', error, dateString);
            return '';
          }
        };

        const formattedData = {
          sales_contract_no: result.data.sales_contract_no || '',
          contract_date: formatDateForInput(result.data.contract_date),
          trade_date: formatDateForInput(result.data.trade_date),
          type_of_transaction: result.data.type_of_transaction || '',
          tel: result.data.tel || '',
          domestic_buyer_id: result.data.domestic_buyer_id || '',
          commodity_id: result.data.commodity_id || '',
          emd: result.data.emd || '',
          contract_quantity_mt: result.data.contract_quantity_mt || '',
          sale_price_per_mt: result.data.sale_price_per_mt || '',
          delivery_point: result.data.delivery_point || '',
          delivery_start_date: formatDateForInput(result.data.delivery_start_date),
          delivery_end_date: formatDateForInput(result.data.delivery_end_date),
          payment_terms: result.data.payment_terms || '',
          broker_id: result.data.broker_id || '',
          penalty_clause: result.data.penalty_clause || '',
          arbitration_clause: result.data.arbitration_clause || '',
          remarks: result.data.remarks || '',
          created_by: result.data.created_by || '',
          order_id: result.data.order_id || '',
          cma_id: result.data.cha_cma_id || '',
          warehouse_id: result.data.warehouse_id || '',
          cha_cma_id: result.data.cha_cma_id || '',
          kribhco_state_code: result.data.kribhco_state_code || '',
          warehouse_charges: result.data.warehouse_charges || '',
          debit_notes: result.data.debit_notes && result.data.debit_notes.length > 0
            ? result.data.debit_notes.map(dn => ({
                dn_id: dn.dn_id,
                dn_of_account: dn.dn_of_account || '',
                dn_date: formatDateForInput(dn.dn_date),
                dn_amount: dn.dn_amount || ''
              }))
            : (result.data.dn_of_account ? [{
                dn_of_account: result.data.dn_of_account || '',
                dn_date: formatDateForInput(result.data.dn_date),
                dn_amount: result.data.dn_amount || ''
              }] : [{ dn_of_account: '', dn_date: '', dn_amount: '' }]),
          closing_date: formatDateForInput(result.data.closing_date),
          vessel_name: result.data.vessel_name || ''
        };

        setFormData(formattedData);

        if (result.data.order_id) {
          // Fetch commodities but don't auto-select in edit mode (id check prevents auto-selection)
          fetchCommoditiesByPurchaseContract(result.data.order_id);
          fetchVesselsByPurchaseContract(result.data.order_id);
        }
      }
    } catch (err) {
      console.error('Error fetching contract:', err);
      setMessage({ type: 'error', text: 'Failed to load contract for edit' });
    }
  }, [fetchCommoditiesByPurchaseContract]);

  const fetchVesselsByPurchaseContract = async (orderId) => {
    if (!orderId) {
      setVessels([]);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-vessels-by-purchase-contract?order_id=${orderId}`, { credentials: 'include' });
      const data = await res.json();
      setVessels(data.data || []);
    } catch (err) {
      console.error('Error fetching vessels by purchase contract:', err);
      setVessels([]);
    }
  };

  useEffect(() => {
    fetchDropdowns();
    fetchImportOrders();
    fetchCmas();
    if (selectedType === 'CHA') {
      fetchChas();
    }
  }, [selectedType]);

  useEffect(() => {
    if (selectedType === 'CHA' && chas.length === 0) {
      fetchChas();
    }
  }, [selectedType, chas.length]);

  // Load contract for edit - only run once when id changes
  useEffect(() => {
    if (id) {
      fetchContractForEdit(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  
  // Set selected purchase contract when importOrders and formData.order_id are available
  useEffect(() => {
    if (id && formData.order_id && importOrders.length > 0 && !selectedPurchaseContract) {
      const selectedOrder = importOrders.find(order => order.order_id === parseInt(formData.order_id));
      if (selectedOrder) {
        setSelectedPurchaseContract(selectedOrder);
      }
    }
  }, [id, formData.order_id, importOrders, selectedPurchaseContract]);

  const fetchDropdowns = async () => {
    try {
      const [buyersRes, commoditiesRes, brokersRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/masters/dropdown-domestic-buyers`, { credentials: 'include' }),
        fetch(`${API_BASE_URL}/api/masters/commodity-get`, { credentials: 'include' }),
        fetch(`${API_BASE_URL}/api/masters/dropdown-sale-brokers`, { credentials: 'include' })
      ]);
      const buyersData = await buyersRes.json();
      const commoditiesData = await commoditiesRes.json();
      const brokersData = await brokersRes.json();
      setBuyers(buyersData.data || []);
      setCommodities(commoditiesData.data || []);
      setBrokers(brokersData.data || []);
    } catch (err) {
      console.error('Error fetching dropdowns:', err);
    }
  };

  const fetchImportOrders = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-import-order`, { credentials: 'include' });
      const data = await res.json();
      setImportOrders(data.data || []);
    } catch (err) {
      console.error('Error fetching import orders:', err);
    }
  };

  const fetchCmas = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-cma-list`, { credentials: 'include' });
      const data = await res.json();
      setCmas(data.data || []);
    } catch (err) {
      console.error('Error fetching CMAs:', err);
    }
  };

  const fetchChas = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-cha-list`, { credentials: 'include' });
      const data = await res.json();
      setChas(data.data || []);
    } catch (err) {
      setChas([]);
    }
  };

  const fetchWarehousesByCma = async (cmaId) => {
    if (!cmaId) {
      setWarehouses([]);
      setFormData(prev => ({ ...prev, delivery_point: '' }));
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-warehouse-list-by-cma?cha_cma_id=${cmaId}`, { 
        credentials: 'include' 
      });
      
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      
      const data = await res.json();
      setWarehouses(data.data || []);
      setFormData(prev => ({ ...prev, delivery_point: '' }));
    } catch (err) {
      console.error('Error fetching warehouses:', err);
      setWarehouses([]);
      setFormData(prev => ({ ...prev, delivery_point: '' }));
    }
  };

  const fetchWarehousesByCha = async (chaId) => {
    if (!chaId) {
      setChaWarehouses([]);
      setFormData(prev => ({ ...prev, delivery_point: '' }));
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-warehouse-list-by-cha?cha_cma_id=${chaId}`, { credentials: 'include' });
      const data = await res.json();
      setChaWarehouses(data.data || []);
      setFormData(prev => ({ ...prev, delivery_point: '' }));
    } catch (err) {
      setChaWarehouses([]);
      setFormData(prev => ({ ...prev, delivery_point: '' }));
    }
  };

  // Date fields that must not allow future dates - selection only, no manual typing
  const DATE_FIELDS_NO_FUTURE = ['contract_date', 'trade_date', 'closing_date'];

  const getMaxDate = () => new Date().toISOString().split('T')[0]; // YYYY-MM-DD

  const preventDateTyping = (e) => {
    if (e.key !== 'Tab' && e.key !== 'Escape') e.preventDefault();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (DATE_FIELDS_NO_FUTURE.includes(name) && value) {
      const maxDate = getMaxDate();
      if (value > maxDate) {
        setMessage({ type: 'error', text: 'Future dates are not allowed for this field.' });
        return;
      }
    }
    setMessage(prev => (prev?.type === 'error' && prev?.text === 'Future dates are not allowed for this field.' ? null : prev));
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'order_id') {
      const selectedOrder = importOrders.find(order => order.order_id === parseInt(value));
      setSelectedPurchaseContract(selectedOrder);
      
      if (selectedOrder) {
        fetchCommoditiesByPurchaseContract(selectedOrder.order_id);
        fetchVesselsByPurchaseContract(selectedOrder.order_id);
      } else {
        setFilteredCommodities([]);
        setVessels([]);
        setFormData(prev => ({ ...prev, commodity_id: '', vessel_name: '' }));
      }
    }
    
    if (name === 'contract_quantity_mt' && message && message.type === 'error' && message.text.includes('Contract Quantity')) {
      setMessage(null);
    }
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
    if (type === 'CMA') {
      setFormData(prev => ({ ...prev, cma_id: '', cha_cma_id: '', delivery_point: '' }));
      setWarehouses([]);
    } else {
      setFormData(prev => ({ ...prev, cha_cma_id: '', cma_id: '', delivery_point: '' }));
      setWarehouses([]);
    }
  };

  const handleDebitNoteChange = (index, field, value) => {
    if (field === 'dn_date' && value) {
      const maxDate = getMaxDate();
      if (value > maxDate) {
        setMessage({ type: 'error', text: 'Future dates are not allowed for Debit Note Date.' });
        return;
      }
    }
    setMessage(prev => (prev?.type === 'error' && prev?.text === 'Future dates are not allowed for Debit Note Date.' ? null : prev));
    
    setFormData(prev => {
      const updatedNotes = [...prev.debit_notes];
      updatedNotes[index] = { ...updatedNotes[index], [field]: value };
      return { ...prev, debit_notes: updatedNotes };
    });
  };

  const addDebitNoteRow = () => {
    setFormData(prev => ({
      ...prev,
      debit_notes: [...prev.debit_notes, { dn_of_account: '', dn_date: '', dn_amount: '' }]
    }));
  };

  const removeDebitNoteRow = (index) => {
    setFormData(prev => {
      const updatedNotes = prev.debit_notes.filter((_, i) => i !== index);
      const finalNotes = updatedNotes.length > 0 ? updatedNotes : [{ dn_of_account: '', dn_date: '', dn_amount: '' }];
      return { ...prev, debit_notes: finalNotes };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    const maxDate = getMaxDate();
    for (const field of DATE_FIELDS_NO_FUTURE) {
      const val = formData[field];
      if (val && val > maxDate) {
        setMessage({ type: 'error', text: 'Future dates are not allowed for Contract Date, Trade Date, or Closing Date.' });
        setLoading(false);
        return;
      }
    }

    // Validate Debit Note dates
    for (let i = 0; i < formData.debit_notes.length; i++) {
      const dn = formData.debit_notes[i];
      if (dn.dn_date && dn.dn_date > maxDate) {
        setMessage({ type: 'error', text: `Future dates are not allowed for Debit Note Date in row ${i + 1}.` });
        setLoading(false);
        return;
      }
    }
    
    if (vessels && vessels.length > 0 && !formData.vessel_name) {
      setMessage({ type: 'error', text: 'Please select a Vessel Name.' });
      setLoading(false);
      return;
    }

    if (selectedPurchaseContract && formData.contract_quantity_mt) {
      const contractQty = parseFloat(formData.contract_quantity_mt);
      let availableQty = 0;
      let targetName = 'Import Order';
      
      if (vessels && vessels.length > 0 && formData.vessel_name) {
        const selectedVessel = vessels.find(v => v.vessel_name === formData.vessel_name);
        if (selectedVessel) {
           const remainingQty = parseFloat(selectedVessel.available_qty);
           availableQty = id ? remainingQty + originalContractQuantity : remainingQty;
           targetName = `Vessel ${formData.vessel_name}`;
        }
      } else {
        const remainingQty = parseFloat(selectedPurchaseContract.remaining_qty_mt);
        availableQty = id ? remainingQty + originalContractQuantity : remainingQty;
      }
      
      if (contractQty > availableQty) {
        setMessage({ 
          type: 'error', 
          text: `Contract Quantity (${contractQty} MT) cannot exceed Available Quantity (${availableQty.toFixed(3)} MT) for ${targetName}.` 
        });
        setLoading(false);
        return;
      }
    }
    
    try {
      const submitData = { 
        ...formData,
        cha_cma_id: formData.cha_cma_id,
        cma_id: undefined
      };
      
      const url = id 
        ? `${API_BASE_URL}/api/sales/sale-contract/${id}`
        : `${API_BASE_URL}/api/sales/sale-contract-post`;
        
      const method = id ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(submitData),
      });
      
      const data = await res.json();
      
      if (res.ok) {
        if (id) {
          navigate(-1);
          return;
        }

        setMessage({ 
          type: 'success', 
          text: 'Sales Contract Created Successfully!'
        });
        
        if (selectedPurchaseContract && formData.contract_quantity_mt) {
          const newRemainingQty = parseFloat(selectedPurchaseContract.remaining_qty_mt) - parseFloat(formData.contract_quantity_mt);
          
          setSelectedPurchaseContract(prev => ({
            ...prev,
            remaining_qty_mt: newRemainingQty.toFixed(3)
          }));
          
          setImportOrders(prevOrders => 
            prevOrders.map(order => 
              order.order_id === parseInt(formData.order_id)
                ? { ...order, remaining_qty_mt: newRemainingQty.toFixed(3) }
                : order
            )
          );
        }
        
        fetchImportOrders();
        
        if (!id) {
          setFormData({
            sales_contract_no: '', 
            contract_date: '', 
            trade_date: '', 
            type_of_transaction: '', 
            tel: '', 
            domestic_buyer_id: '', 
            commodity_id: '', 
            emd: '', 
            contract_quantity_mt: '', 
            sale_price_per_mt: '', 
            delivery_point: '', 
            delivery_start_date: '', 
            delivery_end_date: '', 
            payment_terms: '', 
            broker_id: '', 
            penalty_clause: '', 
            arbitration_clause: '', 
            remarks: '', 
            created_by: '', 
            order_id: '', 
            cma_id: '', 
            warehouse_id: '', 
            cha_cma_id: '',
            kribhco_state_code: '',
            warehouse_charges: '',
            debit_notes: [{ dn_of_account: '', dn_date: '', dn_amount: '' }],
            closing_date: '',
            vessel_name: ''
          });
          
          setSelectedPurchaseContract(null);
          setFilteredCommodities([]);
          setVessels([]);
        }
      } else {
        if (data.duplicate) {
          setMessage({ 
            type: 'error', 
            text: 'Sales Contract Number already exists. Please use a unique number.' 
          });
        } else {
          setMessage({ 
            type: 'error', 
            text: data.error || 'Something went wrong' 
          });
        }
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Server Error' });
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-4 bg-white">
      <div className="flex justify-between items-center border-b pb-3 mb-5">
        <h2 className="text-lg font-medium text-gray-800">Sales Contract Entry</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#3899cf] border border-[#10a5e9] px-3 py-1 text-xs rounded font-normal hover:bg-[#10a5e9] hover:text-white hover:border-[#10a5e9] transition"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-700 mb-1">Purchase Contract No</label>
            <SearchableDropdown
              options={importOrders}
              value={formData.order_id}
              onChange={(value) => {
                setFormData(prev => ({ ...prev, order_id: value }));
                const selectedOrder = importOrders.find(order => order.order_id === parseInt(value));
                setSelectedPurchaseContract(selectedOrder);
                
                if (selectedOrder) {
                  setFormData(prev => ({ ...prev, commodity_id: '', vessel_name: '' }));
                  fetchCommoditiesByPurchaseContract(selectedOrder.order_id);
                  fetchVesselsByPurchaseContract(selectedOrder.order_id);
                } else {
                  setFilteredCommodities([]);
                  setVessels([]);
                  setFormData(prev => ({ ...prev, commodity_id: '', vessel_name: '' }));
                }
              }}
              placeholder="Search Purchase Contract"
              displayKey="purchase_contract_no"
              valueKey="order_id"
              required
              customDisplay={(item) => `${item.purchase_contract_no}${item.vessel_name ? ` (${item.vessel_name})` : ''}`}
            />
          </div>

          {vessels && vessels.length > 0 && (
            <div className="bg-blue-50/50 p-3 rounded border border-blue-100">
              <label className="block text-sm font-medium text-blue-800 mb-1">
                Select Vessel Name (Required)
              </label>
              <select 
                name="vessel_name" 
                value={formData.vessel_name} 
                onChange={handleChange} 
                className="w-full border border-blue-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-500 rounded"
                required
              >
                <option value="">-- Select Vessel --</option>
                {vessels.map((v, i) => (
                  <option key={i} value={v.vessel_name} disabled={v.available_qty <= 0 && formData.vessel_name !== v.vessel_name}>
                    {v.vessel_name} (Available: {parseFloat(v.available_qty).toFixed(3)} MT)
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm text-gray-700 mb-1">Contract Quantity (MT)</label>
            <input 
              type="number" 
              step="0.01" 
              name="contract_quantity_mt" 
              value={formData.contract_quantity_mt} 
              onChange={handleChange} 
              className={`w-full border px-3 py-1.5 text-sm bg-white focus:outline-none rounded-none ${
                selectedPurchaseContract && formData.contract_quantity_mt && 
                parseFloat(formData.contract_quantity_mt) > (
                  formData.vessel_name && vessels.find(v => v.vessel_name === formData.vessel_name) 
                  ? parseFloat(vessels.find(v => v.vessel_name === formData.vessel_name).available_qty) + (id ? originalContractQuantity : 0)
                  : parseFloat(selectedPurchaseContract.remaining_qty_mt) + (id ? originalContractQuantity : 0)
                )
                  ? 'border-red-400 focus:border-red-400' 
                  : 'border-gray-300 focus:border-blue-400'
              }`} 
              required 
            />
            {selectedPurchaseContract && (
              <div className="text-xs text-gray-600 mt-1 bg-blue-50 p-2 rounded border border-blue-100">
                <div className="grid grid-cols-2 gap-1 mb-1 border-b border-blue-200 pb-1">
                  <div>Import Order Qty: <span className="font-medium text-gray-800">{selectedPurchaseContract.io_qty_mt} MT</span></div>
                  <div>Arrived B/L Qty: <span className="font-medium text-gray-800">{selectedPurchaseContract.bl_qty_mt} MT</span></div>
                  <div className="col-span-2">Parent Purchase Contract: <span className="font-medium text-gray-600">{selectedPurchaseContract.total_qty_mt} MT</span></div>
                </div>
                
                <div 
                  className="text-green-600 cursor-pointer hover:underline inline-block mb-1"
                  onClick={handleViewClosedContracts}
                  title="Click to view details of recovered quantity"
                >
                  Freed/Returned Qty (from Closed Contracts): <span className="font-medium">{selectedPurchaseContract.freed_qty_mt || 0} MT</span>
                </div>
                {id ? (
                  <>
                    <div>Current Contract Quantity: <span className="font-medium">{originalContractQuantity.toFixed(3)} MT</span></div>
                    {formData.vessel_name && vessels.find(v => v.vessel_name === formData.vessel_name) ? (
                      <div className="text-blue-700">Available Quantity for Edit ({formData.vessel_name}): <span className="font-medium">{(parseFloat(vessels.find(v => v.vessel_name === formData.vessel_name).available_qty) + originalContractQuantity).toFixed(3)} MT</span></div>
                    ) : (
                      <div className="text-blue-700">Available Quantity for Edit: <span className="font-medium">{(parseFloat(selectedPurchaseContract.remaining_qty_mt) + originalContractQuantity).toFixed(3)} MT</span></div>
                    )}
                  </>
                ) : (
                  formData.vessel_name && vessels.find(v => v.vessel_name === formData.vessel_name) ? (
                    <div className="text-blue-700 font-semibold text-[13px]">Remaining Available Qty ({formData.vessel_name}): <span className="font-bold">{parseFloat(vessels.find(v => v.vessel_name === formData.vessel_name).available_qty).toFixed(3)} MT</span></div>
                  ) : (
                    <div className="text-blue-700 font-semibold text-[13px]">Remaining Available Qty (Order Level): <span className="font-bold">{selectedPurchaseContract.remaining_qty_mt} MT</span></div>
                  )
                )}
                {formData.contract_quantity_mt && selectedPurchaseContract && parseFloat(formData.contract_quantity_mt) > (
                  formData.vessel_name && vessels.find(v => v.vessel_name === formData.vessel_name) 
                  ? parseFloat(vessels.find(v => v.vessel_name === formData.vessel_name).available_qty) + (id ? originalContractQuantity : 0)
                  : parseFloat(selectedPurchaseContract.remaining_qty_mt) + (id ? originalContractQuantity : 0)
                ) && (
                  <span className="text-red-600 font-medium block mt-1">⚠️ Quantity exceeds remaining available limit</span>
                )}
              </div>
            )}
          </div>
          
          <div>
            <label className="block text-sm text-gray-700 mb-1">Sales Contract No</label>
            <input type="text" name="sales_contract_no" value={formData.sales_contract_no} onChange={handleChange} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Contract Date</label>
            <input type="date" name="contract_date" value={formData.contract_date} onChange={handleChange} onKeyDown={preventDateTyping} max={getMaxDate()} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none cursor-pointer" title="Select date from calendar only (future dates not allowed)" required />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Trade Date</label>
            <input type="date" name="trade_date" value={formData.trade_date} onChange={handleChange} onKeyDown={preventDateTyping} max={getMaxDate()} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none cursor-pointer" title="Select date from calendar only (future dates not allowed)" />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Type of Transaction</label>
            <select name="type_of_transaction" value={formData.type_of_transaction} onChange={handleChange} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none">
              <option value="">-- Select Type --</option>
              <option value="Domestic Sale">Domestic Sale</option>
              <option value="High Sea Sale">High Sea Sale</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">TEL</label>
            <input type="text" name="tel" value={formData.tel} onChange={handleChange} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" placeholder="Total Export License number" />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Domestic Buyer</label>
            <select name="domestic_buyer_id" value={formData.domestic_buyer_id} onChange={handleChange} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" required>
              <option value="">-- Select Buyer --</option>
              {buyers.map(b => <option key={b.domestic_buyer_id} value={b.domestic_buyer_id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Sale Broker</label>
            <select name="broker_id" value={formData.broker_id} onChange={handleChange} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none">
              <option value="">-- Select Broker --</option>
              {brokers.map(b => <option key={b.broker_id} value={b.broker_id}>{b.name}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">
              Commodity
              {filteredCommodities.length === 1 && (
                <span className="text-xs text-green-600 ml-2">✓ Auto-selected</span>
              )}
            </label>
            <SearchableDropdown
              options={filteredCommodities.length > 0 ? filteredCommodities : commodities}
              value={formData.commodity_id}
              onChange={(value) => {
                setFormData(prev => ({ ...prev, commodity_id: value }));
              }}
              placeholder={filteredCommodities.length > 0 ? "Select Commodity (Filtered by Purchase Contract)" : "Select Commodity"}
              displayKey="name"
              valueKey="commodity_id"
              required
              customDisplay={(item) => `${item.name} (${item.origin || 'N/A'})`}
            />
            {filteredCommodities.length > 0 && (
              <div className="text-xs text-blue-600 mt-1">
                Showing commodities from selected purchase contract
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Sale Price per MT</label>
            <input type="number" step="0.01" name="sale_price_per_mt" value={formData.sale_price_per_mt} onChange={handleChange} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">EMD</label>
            <input type="number" step="0.01" name="emd" value={formData.emd} onChange={handleChange} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" />
          </div>

          <div className="md:col-span-2 flex gap-6 items-center mb-2">
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={selectedType === 'CMA'} onChange={() => handleTypeChange('CMA')} /> CMA
            </label>
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={selectedType === 'CHA'} onChange={() => handleTypeChange('CHA')} /> CHA
            </label>
          </div>

          {selectedType === 'CMA' && (
            <>
              <div>
                <label className="block text-sm text-gray-700 mb-1">CMA</label>
                <select 
                  name="cma_id" 
                  value={formData.cha_cma_id || ''} 
                  onChange={e => {
                    const value = e.target.value;
                    setFormData(prev => ({ 
                      ...prev, 
                      cha_cma_id: value,
                      cma_id: value,
                      delivery_point: ''
                    }));
                    fetchWarehousesByCma(value);
                  }} 
                  className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" 
                >
                  <option value="">-- Select CMA --</option>
                  {cmas.map(cma => (
                    <option key={cma.cha_cma_id} value={cma.cha_cma_id}>
                      {cma.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Delivery Point (Warehouse)</label>
                <select 
                  name="delivery_point" 
                  value={formData.delivery_point || ''} 
                  onChange={handleChange} 
                  className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" 
                  required
                >
                  <option value="">-- Select Warehouse --</option>
                  {warehouses.map(wh => (
                    <option key={wh.warehouse_id} value={wh.warehouse_id}>
                      {wh.name} ({wh.location})
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {selectedType === 'CHA' && (
            <>
              <div>
                <label className="block text-sm text-gray-700 mb-1">CHA</label>
                <select 
                  name="cha_cma_id" 
                  value={formData.cha_cma_id || ''} 
                  onChange={e => {
                    const value = e.target.value;
                    setFormData(prev => ({ 
                      ...prev, 
                      cha_cma_id: value,
                      cma_id: value,
                      delivery_point: ''
                    }));
                    fetchWarehousesByCha(value);
                  }} 
                  className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" 
                >
                  <option value="">-- Select CHA --</option>
                  {chas.map(cha => (
                    <option key={cha.cha_cma_id} value={cha.cha_cma_id}>
                      {cha.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Warehouse/Port</label>
                <select 
                  name="delivery_point" 
                  value={formData.delivery_point || ''} 
                  onChange={handleChange} 
                  className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" 
                  required
                >
                  <option value="">-- Select Warehouse/Port --</option>
                  {chaWarehouses.map(wh => (
                    <option key={wh.warehouse_id || wh.port_id} value={wh.warehouse_id || wh.port_id}>
                      {wh.name || wh.warehouse_name || wh.port_name}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div>
            <label className="block text-sm text-gray-700 mb-1">Delivery Start Date</label>
            <input type="date" name="delivery_start_date" value={formData.delivery_start_date} onChange={handleChange} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Delivery End Date</label>
            <input type="date" name="delivery_end_date" value={formData.delivery_end_date} onChange={handleChange} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Kribhco State</label>
            <select 
              name="kribhco_state_code" 
              value={formData.kribhco_state_code} 
              onChange={handleChange} 
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
            >
              <option value="">-- Select State --</option>
              {states.map(state => (
                <option key={state.code} value={state.code}>
                  {state.name} ({state.code})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Warehouse Charges</label>
            <input 
              type="number" 
              step="0.01" 
              name="warehouse_charges" 
              value={formData.warehouse_charges} 
              onChange={handleChange} 
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" 
              placeholder="Enter warehouse charges"
            />
          </div>

          <div className="md:col-span-2 border-t pt-4 mt-2">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-medium text-gray-800">Debit Notes (DN)</h3>
              <button
                type="button"
                onClick={addDebitNoteRow}
                className="text-xs bg-[#3899cf] text-white px-3 py-1.5 rounded hover:bg-[#0e8ac2] transition"
              >
                + Add Debit Note Row
              </button>
            </div>
            
            <div className="space-y-3">
              {formData.debit_notes.map((dn, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end bg-gray-50 p-3 border border-gray-200 relative">
                  {formData.debit_notes.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeDebitNoteRow(index)}
                      className="absolute top-2 right-2 text-red-500 hover:text-red-700 text-xs font-medium"
                    >
                      Remove
                    </button>
                  )}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">DN of Account</label>
                    <input
                      type="text"
                      value={dn.dn_of_account}
                      onChange={(e) => handleDebitNoteChange(index, 'dn_of_account', e.target.value)}
                      className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
                      placeholder="Enter DN of Account"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">DN Date</label>
                    <input
                      type="date"
                      value={dn.dn_date}
                      onChange={(e) => handleDebitNoteChange(index, 'dn_date', e.target.value)}
                      onKeyDown={preventDateTyping}
                      max={getMaxDate()}
                      className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">DN Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={dn.dn_amount}
                      onChange={(e) => handleDebitNoteChange(index, 'dn_amount', e.target.value)}
                      className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
                      placeholder="Enter DN Amount"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Closing Date</label>
            <input 
              type="date" 
              name="closing_date" 
              value={formData.closing_date} 
              onChange={handleChange} 
              onKeyDown={preventDateTyping}
              max={getMaxDate()}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none cursor-pointer" 
              title="Select date from calendar only (future dates not allowed)"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Payment Terms</label>
            <textarea name="payment_terms" value={formData.payment_terms} onChange={handleChange} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none resize-none" rows={2}></textarea>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Arbitration Clause</label>
            <textarea name="arbitration_clause" value={formData.arbitration_clause} onChange={handleChange} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none resize-none" rows={2}></textarea>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Penalty Clause</label>
            <input type="text" name="penalty_clause" value={formData.penalty_clause} onChange={handleChange} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Remarks</label>
            <textarea name="remarks" value={formData.remarks} onChange={handleChange} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none resize-none" rows={2}></textarea>
          </div>
        </div>
        <div className="pt-2 flex items-center gap-3">
          <button type="submit" className="px-4 py-2 text-xs bg-[#3899cf] text-white font-normal rounded border border-[#10a5e9] hover:bg-[#0e8ac2] transition disabled:opacity-60" style={{ minWidth: '110px' }} disabled={loading}>
            {loading ? 'Saving...' : 'Save Sales Contract'}
          </button>
          {message && (
            <div className={`text-sm ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>{message.text}</div>
          )}
        </div>
      </form>
      
      {/* Modal for Closed Contracts Details */}
      <ViewModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        title="Closed Contracts (Quantity Recovered)"
        size="lg"
      >
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contract No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Closing Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Original Qty</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Utilized Qty (SO)</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-green-600 uppercase tracking-wider">Freed Qty</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {closedContracts.length > 0 ? (
                <>
                  {closedContracts.map((contract, index) => (
                    <tr key={index} className="hover:bg-gray-50 border-b border-gray-100">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{contract.sales_contract_no || 'N/A'}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{contract.closing_date ? new Date(contract.closing_date).toLocaleDateString('en-GB') : ''}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">{parseFloat(contract.contract_quantity_mt).toFixed(3)} MT</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">{parseFloat(contract.so_qty_mt).toFixed(3)} MT</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-green-600">{parseFloat(contract.freed_qty_mt).toFixed(3)} MT</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                    <td colSpan="2" className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 uppercase">Total:</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                      {closedContracts.reduce((acc, curr) => acc + parseFloat(curr.contract_quantity_mt || 0), 0).toFixed(3)} MT
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                      {closedContracts.reduce((acc, curr) => acc + parseFloat(curr.so_qty_mt || 0), 0).toFixed(3)} MT
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-green-700">
                      {closedContracts.reduce((acc, curr) => acc + parseFloat(curr.freed_qty_mt || 0), 0).toFixed(3)} MT
                    </td>
                  </tr>
                </>
              ) : (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-gray-500">No closed contracts found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </ViewModal>
    </div>
  );
};

export default SaleContractForm;
